<template>
    <div class="documents-table">
        <ModeSwitcher
            ref="modeSwitcher"
            :main-mode-text="$t('create')"
            :create-mode-text="$t('upload_new_document')"
            :edit-mode-text="$t('edit_document')"
            @reset="editDocumentId = null"
        >
            <template v-slot:main-mode>
                <v-data-table-server
                    :headers="headers"
                    :items="useItems"
                    :items-per-page="-1"
                    :items-per-page-options="[-1]"
                    :loading="isLoading"
                    :items-length="useItems.length"
                    disable-sort
                    density="compact"
                    color="primary"
                    class="elevation-1 table-server"
                    data-cy="documents-table"
                >
                    <template v-slot:item.name="{item}">
                        <div class="d-flex justify-space-between align-center flex-nowrap">
                            <strong>{{ item.name }}</strong>
                            <v-btn
                                color="primary"
                                class="ml-4"
                                variant="text"
                                icon="mdi-file-download-outline"
                                v-bind="props"
                                data-cy="download-document"
                                @click="downloadDocument(item)"
                            />
                        </div>
                    </template>
                    <template v-slot:item.active="{item}">
                        <v-switch
                            v-model="item.active"
                            color="primary"
                            :label="item.active ? $t('active') : $t('inactive')"
                            hide-details
                            :data-cy="`document-${item.id}`"
                            @update:model-value="inactivateDocument(item)"
                        />
                    </template>
                </v-data-table-server>
            </template>
            <template v-slot:additional-mode>
                <DocumentForm
                    :document-types="documentTypes"
                    :company-id="companyId"
                    :attach-to-id="attachToId"
                    @save="save()"
                    @cancel="modeSwitcher.goToTable()"
                />
            </template>
        </ModeSwitcher>
    </div>
</template>

<script setup lang="ts">
    import type {SelectOptionType} from '~/utils/types/types'
    import {downloadData} from '~/utils/helpers'
    import DocumentForm from '~/components/account/documents/DocumentForm.vue'

    const props = defineProps<{
        companyId: number
        attachToId: number
        documentTypes: SelectOptionType[]
    }>()
    const isLoading = ref<boolean>(false)
    const documents = ref([])
    const editDocumentId = ref<number>(null)
    const modeSwitcher = ref(null)

    const app = useNuxtApp()
    const {t} = useI18n()

    onMounted(async () => {
        await loadDocuments()
    })

    const headers: ComputedRef<{title: string; value: string}[]> = computed(() => {
        const headers: {title: string; value: string}[] = [
            {title: t('name'), value: 'name'},
            {title: t('document_type'), value: 'documentType'},
            {title: t('user'), value: 'user'},
            {title: t('upload_date'), value: 'creationTimestamp'},
            {title: t('validity'), value: 'validity'},
            {title: t('status'), value: 'active'},
        ]
        return headers
    })

    async function loadDocuments(): Promise<void> {
        try {
            isLoading.value = true
            let query = {}
            if (props.companyId === props.attachToId) query = {organizationId: props.attachToId}
            if (props.companyId !== props.attachToId) query = {userId: props.attachToId}

            documents.value = await app.$api.documents.get(query)
        } catch (e) {
            documents.value = []
        } finally {
            isLoading.value = false
        }
    }

    const useItems = computed(() => {
        return documents.value.map(
            ({name, creationTimestamp, validFrom, validTo, documentType, creator, active, id}) => {
                const dateFrom = validFrom ? app.$filters.dateShortFormat(validFrom) : ''
                const dateTo = validTo ? app.$filters.dateShortFormat(validTo) : ''
                const validity = `${dateFrom} ${dateFrom && dateTo ? '-' : ''} ${dateTo}`.trim()
                const user =
                    `${creator?.middleName || ''} ${creator?.firstName || ''} ${creator?.lastName || ''}`.trim()

                return {
                    name,
                    creationTimestamp: creationTimestamp ? app.$filters.dateTimeFormat(creationTimestamp) : '',
                    validity,
                    documentType: t(`documentTypes.${documentType}`),
                    user,
                    active,
                    id,
                }
            }
        )
    })

    const inactivateDocument = async (doc): Promise<void> => {
        try {
            isLoading.value = true
            await app.$api.documents.put(doc.id, {})
            app.$toast.success(t('success_message.saved_successfully'))
            await loadDocuments()
        } catch (e) {
            app.$toast.error(t('data_update_fail'))
        } finally {
            isLoading.value = false
        }
    }

    const downloadDocument = async doc => {
        const rs = await app.$api.documents.get({id: doc.id})
        const decodedData = atob(rs[0].encodedData)
        const data = new Uint8Array(decodedData.split('').map(char => char.charCodeAt(0)))
        downloadData(data, doc.name)
    }

    const save = async (): Promise<void> => {
        await loadDocuments()
        modeSwitcher.value.goToTable()
    }
</script>
